import { getCountryFlag } from 'omega-rtc/utils/country-flag';
import {
  AddUniversal,
  NumberedListUniversal,
  SendUniversal,
} from '@duxton-web/icons';
import {
  Button,
  DatePicker,
  Divider,
  Dropdown,
  Flex,
  Form,
  Input,
  Select,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import React from 'react';
import { useSelector } from 'react-redux';
import DocumentTitle from 'react-document-title';

import { STATUS } from 'omega-rtc/constants/ucd';

import {
  getJourneyCampaignProductOfferingOptions,
  JOURNEY_CAMPAIGN_VERTICAL_OPTIONS,
} from './JourneyCampaignMetadataOptions';
import { getTridentPortalCountries, isValidGoogleSheetUrl } from './CreateJourneyCampaignGroup.utils';

const GOOGLE_SHEET_SOURCE = 'google-sheet';

const AUDIENCE_TYPE = {
  passenger: 'passenger',
  dax: 'driver',
  mex: 'foodmax',
};

const AUDIENCE_TYPE_OPTIONS = [
  { label: 'Passenger', value: AUDIENCE_TYPE.passenger },
  { label: 'DAX', value: AUDIENCE_TYPE.dax },
  { label: 'MEX', value: AUDIENCE_TYPE.mex },
];

const JOURNEY_TYPE_OPTIONS = [
  {
    label: (
      <Flex align="center" gap="8px">
        <SendUniversal aria-hidden="true" />
        <span>Single Touch</span>
      </Flex>
    ),
    value: 'single-touch',
  },
  {
    label: (
      <Flex align="center" gap="8px">
        <NumberedListUniversal aria-hidden="true" />
        <span>Multi Touch</span>
      </Flex>
    ),
    value: 'multi-touch',
  },
];

export type JourneyCampaignCountryOption = {
  code?: string;
  countryID: number;
  name: string;
};

export type JourneyCampaignCountryFormValue = {
  campaignID?: string;
  countryID: number;
  projectID?: string;
  source: string;
  sourceUrl?: string;
};

export type JourneyCampaignFormValues = {
  audienceType: string;
  campaignName: string;
  countries: JourneyCampaignCountryFormValue[];
  endDate: Dayjs;
  journeyType: string;
  productOffering: string;
  startDate: Dayjs;
  vertical: string;
};

type CreateJourneyCampaignAudience = 'PAX' | 'DAX' | 'MEX';

export type CreateJourneyCampaignRequest = {
  audienceType: CreateJourneyCampaignAudience;
  campaigns: Array<{
    countryID: number;
    projectID?: string;
    source: {
      type: 'GOOGLE_SHEET';
      url: string;
    };
  }>;
  endDate: string;
  groupType: 'JOURNEY';
  journeyType: 'SINGLE_TOUCH' | 'MULTI_TOUCH';
  name: string;
  productOffering: string;
  startDate: string;
  vertical: string;
};

const AUDIENCE_REQUEST_VALUES: Record<string, CreateJourneyCampaignAudience> = {
  [AUDIENCE_TYPE.passenger]: 'PAX',
  [AUDIENCE_TYPE.dax]: 'DAX',
  [AUDIENCE_TYPE.mex]: 'MEX',
};

export const buildCreateJourneyCampaignRequest = (
  values: JourneyCampaignFormValues,
): CreateJourneyCampaignRequest => ({
  groupType: 'JOURNEY',
  journeyType: values.journeyType === 'multi-touch' ? 'MULTI_TOUCH' : 'SINGLE_TOUCH',
  name: values.campaignName,
  startDate: values.startDate.format('YYYY-MM-DD'),
  endDate: values.endDate.format('YYYY-MM-DD'),
  audienceType: AUDIENCE_REQUEST_VALUES[values.audienceType],
  vertical: values.vertical,
  productOffering: values.productOffering,
  campaigns: values.countries.map(({ countryID, projectID, sourceUrl = '' }) => ({
    countryID,
    ...(projectID ? { projectID } : {}),
    source: {
      type: 'GOOGLE_SHEET',
      url: sourceUrl,
    },
  })),
});

type CountryFormSectionProps = {
  country: JourneyCampaignCountryOption;
  field: { key: number; name: number };
  isRemovable: boolean;
  onRemove: (name: number) => void;
  status?: string;
};

const CAMPAIGN_FIELDS_LOCKED_MESSAGE = 'All existing campaign statuses must be Draft to modify this field.';

const normalizeCampaignStatus = (status: string) => (
  status.trim().replace(/\s+/g, '_').toUpperCase()
);

const formatCampaignStatus = (status: string) => {
  const normalizedStatus = normalizeCampaignStatus(status);
  if (normalizedStatus === STATUS.pendingApproval) return 'Pending Approval';
  return normalizedStatus.toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getCampaignStatusColor = (status: string) => {
  const normalizedStatus = normalizeCampaignStatus(status);
  if (normalizedStatus === 'ACTIVE') {
    return 'success';
  }
  if (normalizedStatus === STATUS.pendingApproval) {
    return 'processing';
  }
  if (normalizedStatus === 'DRAFT') {
    return 'warning';
  }
  return 'default';
};

const CampaignFieldLockTooltip = ({
  children,
  isLocked,
  style,
}: {
  children: React.ReactNode;
  isLocked: boolean;
  style?: React.CSSProperties;
}) => (
  <Tooltip title={isLocked ? CAMPAIGN_FIELDS_LOCKED_MESSAGE : undefined}>
    <div style={style}>{children}</div>
  </Tooltip>
);

export { getCountryFlag } from 'omega-rtc/utils/country-flag';

export const getJourneyCampaignInitialValues = (): JourneyCampaignFormValues => ({
  audienceType: AUDIENCE_TYPE.passenger,
  campaignName: '',
  countries: [],
  endDate: dayjs().add(10, 'years').startOf('day'),
  journeyType: 'single-touch',
  productOffering: '',
  startDate: dayjs().add(1, 'day').startOf('day'),
  vertical: '',
});

const CountryFormSection = ({
  country,
  field,
  isRemovable,
  onRemove,
  status,
}: CountryFormSectionProps) => {
  return (
    <div
      style={{
        border: '1px solid #d8d8d8',
        borderRadius: '8px',
        padding: '20px',
      }}
    >
      <Flex justify="space-between" align="center">
        <Typography.Title level={4}>
          <span aria-hidden="true">{getCountryFlag(country.code)}</span>{' '}
          {country.name}
        </Typography.Title>
        <Button
          danger
          disabled={!isRemovable}
          title={isRemovable ? undefined : 'Existing countries cannot be removed'}
          type="link"
          onClick={() => onRemove(field.name)}
        >
          Remove
        </Button>
      </Flex>

      {status && (
        <Flex align="center" gap="8px" style={{ marginBottom: '16px' }}>
          <Typography.Body strong>Campaign Status</Typography.Body>
          <Tag color={getCampaignStatusColor(status)}>
            {formatCampaignStatus(status)}
          </Tag>
        </Flex>
      )}

      <Form.Item
        label="Source"
        name={[field.name, 'source']}
        rules={[{ required: true, message: 'Please select a source' }]}
      >
        <Select
          options={[{ label: 'Google Sheet', value: GOOGLE_SHEET_SOURCE }]}
        />
      </Form.Item>

      <Form.Item
        label="Source URL"
        name={[field.name, 'sourceUrl']}
        rules={[
          { required: true, message: 'Please input the Google Sheet URL' },
          {
            validator: (_, value) => (isValidGoogleSheetUrl(value)
              ? Promise.resolve()
              : Promise.reject(new Error('Please input a valid Google Sheet URL'))),
          },
        ]}
      >
        <Input placeholder="https://docs.google.com/spreadsheets/..." />
      </Form.Item>
    </div>
  );
};

interface JourneyCampaignFormProps {
  countries: JourneyCampaignCountryOption[];
  countryStatuses?: Record<number, string>;
  existingCountryIDs?: number[];
  initialValues?: JourneyCampaignFormValues;
  isSubmitting?: boolean;
  mode?: 'create' | 'edit';
  onSubmit?: (values: JourneyCampaignFormValues) => void;
}

export const JourneyCampaignForm = ({
  countries,
  countryStatuses = {},
  existingCountryIDs = [],
  initialValues = getJourneyCampaignInitialValues(),
  isSubmitting = false,
  mode = 'create',
  onSubmit = () => undefined,
}: JourneyCampaignFormProps) => {
  const [form] = Form.useForm();
  const selectedCountries = Form.useWatch('countries', form) || [];
  const selectedVertical = Form.useWatch('vertical', form) || '';
  const productOfferingOptions = getJourneyCampaignProductOfferingOptions(
    selectedVertical,
  );
  const areCampaignFieldsLocked = mode === 'edit'
    && Object.values(countryStatuses).some(
      (status) => normalizeCampaignStatus(status) !== 'DRAFT',
    );

  const selectedCountryIDs = selectedCountries
    .map((country: { countryID?: number }) => country && country.countryID)
    .filter(Boolean);
  const availableCountryOptions = countries
    .filter((country) => !selectedCountryIDs.includes(country.countryID))
    .map((country) => ({
      label: (
        <span>
          <span aria-hidden="true">{getCountryFlag(country.code)}</span>{' '}
          {country.name}
        </span>
      ),
      value: country.countryID,
    }));

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onFinish={onSubmit}
      style={{ width: '100%', maxWidth: '1316px' }}
    >
      <div style={{ maxWidth: '800px' }}>
        <Flex gap="16px">
          <CampaignFieldLockTooltip
            isLocked={areCampaignFieldsLocked}
            style={{ flex: 1 }}
          >
            <Form.Item
              label="Campaign Start Date"
              name="startDate"
              rules={[{ required: true, message: 'Please select the campaign start date' }]}
            >
              <DatePicker disabled={areCampaignFieldsLocked} style={{ width: '100%' }} />
            </Form.Item>
          </CampaignFieldLockTooltip>
          <CampaignFieldLockTooltip
            isLocked={areCampaignFieldsLocked}
            style={{ flex: 1 }}
          >
            <Form.Item
              dependencies={['startDate']}
              label="Campaign End Date"
              name="endDate"
              rules={[
                { required: true, message: 'Please select the campaign end date' },
                ({ getFieldValue }) => ({
                  validator(_, endDate) {
                    const startDate = getFieldValue('startDate');
                    if (!endDate || !startDate || !endDate.isBefore(startDate)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error('Campaign end date cannot be before the start date'),
                    );
                  },
                }),
              ]}
            >
              <DatePicker disabled={areCampaignFieldsLocked} style={{ width: '100%' }} />
            </Form.Item>
          </CampaignFieldLockTooltip>
        </Flex>

        <CampaignFieldLockTooltip
          isLocked={areCampaignFieldsLocked}
        >
          <Form.Item
            label="Campaign Group Name"
            name="campaignName"
            rules={[{ required: true, message: 'Please input the campaign name' }]}
          >
            <Input
              disabled={areCampaignFieldsLocked}
              placeholder="Enter campaign name"
            />
          </Form.Item>
        </CampaignFieldLockTooltip>

        <Form.Item
          label="Audience Type"
          name="audienceType"
          rules={[{ required: true, message: 'Please select an audience type' }]}
        >
          <Select disabled={mode === 'edit'} options={AUDIENCE_TYPE_OPTIONS} />
        </Form.Item>

        <Form.Item
          label="Journey Type"
          name="journeyType"
          rules={[{ required: true, message: 'Please select a journey type' }]}
        >
          <Select options={JOURNEY_TYPE_OPTIONS} />
        </Form.Item>

        <Flex gap="16px">
          <Form.Item
            label="Vertical"
            name="vertical"
            rules={[{ required: true, message: 'Please select a vertical' }]}
            style={{ flex: 1 }}
          >
            <Select
              options={JOURNEY_CAMPAIGN_VERTICAL_OPTIONS}
              placeholder="Select vertical"
              onChange={() => form.setFieldsValue({ productOffering: undefined })}
            />
          </Form.Item>
          <Form.Item
            label="Product Offering"
            name="productOffering"
            rules={[{ required: true, message: 'Please select a product offering' }]}
            style={{ flex: 1 }}
          >
            <Select
              disabled={!selectedVertical}
              options={productOfferingOptions}
              placeholder={selectedVertical
                ? 'Select product offering'
                : 'Select a vertical first'}
            />
          </Form.Item>
        </Flex>
      </div>

      <Divider />
      <Typography.Title level={3}>Countries</Typography.Title>
      <Typography.Body style={{ display: 'block', marginBottom: '16px' }}>
        Add each country and configure its audience source.
      </Typography.Body>

      <Form.List name="countries">
        {(fields, { add, remove }) => (
          <React.Fragment>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 500px), 650px))',
                gap: '16px',
                justifyContent: 'start',
                marginBottom: '16px',
              }}
            >
              {fields.map((field) => {
                const country = countries.find(
                  (candidate) => (
                    selectedCountries[field.name]
                    && candidate.countryID === selectedCountries[field.name].countryID
                  ),
                );

                return country ? (
                  <CountryFormSection
                    key={field.key}
                    country={country}
                    field={field}
                    isRemovable={!existingCountryIDs.includes(country.countryID)}
                    onRemove={remove}
                    status={countryStatuses[country.countryID]}
                  />
                ) : null;
              })}
            </div>
            <Dropdown
              menu={{
                items: availableCountryOptions.map((option) => ({
                  key: String(option.value),
                  label: option.label,
                })),
                onClick: ({ key }) => {
                  const countryID = Number(key);
                  if (!selectedCountryIDs.includes(countryID)) {
                    add({
                      countryID,
                      source: GOOGLE_SHEET_SOURCE,
                    });
                  }
                },
              }}
              trigger={['click']}
            >
              <Button
                type="dashed"
                icon={<AddUniversal />}
                disabled={!availableCountryOptions.length}
              >
                Add country
              </Button>
            </Dropdown>
          </React.Fragment>
        )}
      </Form.List>

      <Divider />
      <Button
        type="primary"
        htmlType="submit"
        disabled={!selectedCountryIDs.length}
        loading={isSubmitting}
      >
        {mode === 'edit' ? 'Save changes' : 'Create Campaign Group'}
      </Button>
    </Form>
  );
};

type UCDCreateJourneyCampaignProps = {
  onSubmit?: (request: CreateJourneyCampaignRequest) => void;
};

export const UCDCreateJourneyCampaign = ({
  onSubmit = () => undefined,
}: UCDCreateJourneyCampaignProps) => {
  const countries = useSelector((state: any) => state.countries.entities || []);

  return (
    <DocumentTitle title="Create Journey Campaign Group - Holos">
      <div className="ucd-basic">
        <Flex align="center" gap="12px" style={{ marginBottom: '24px' }}>
          <Typography.Title level={2}>
            Create Journey Campaign Group
          </Typography.Title>
        </Flex>
        <JourneyCampaignForm
          countries={getTridentPortalCountries(countries)}
          onSubmit={(values) => onSubmit(buildCreateJourneyCampaignRequest(values))}
        />
      </div>
    </DocumentTitle>
  );
};

export default UCDCreateJourneyCampaign;
